import React from 'react';
import { Navigate } from 'react-router-dom';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Heading from '@amzn/meridian/heading';
import { useAppSelector } from '../../../store';
import {
  selectStationAssignment,
  selectHistoricalPlanData,
} from '../../../store/slices/stationAssignmentSlice';
import { useNavigationControl } from '../../../hooks/useNavigationControl';
import { DEFAULT_SHIFT_HALF_OPTIONS, ROUTES } from '../../../utils/constants';
import { GetStationAssignmentsResponse } from '../assignments/models';
import LaborBoard from './components/LaborBoard';
import './index.scss';

/**
 * Associate View Page (a.k.a. "Labor board").
 *
 * A full-viewport, read-only page intended for display on a warehouse
 * monitor or TV so associates can see their station assignments for the
 * current shift. A manager opens this view from the "Associate view"
 * button on the Published Assignment page, and returns via the
 * "Manager View" button rendered in this page's header.
 *
 * Responsibilities:
 *   - Guard against direct entry: if `historicalPlanData` is null in
 *     the Redux store, redirect to `/`.
 *   - Read the pre-fetched `historicalPlanData` from Redux (stored by the
 *     Published page after a successful `getStationAssignments` call) and
 *     render directly — no additional API call needed.
 *   - Render a header row with the title "Labor board" on the left and
 *     a "Manager View" button on the right, followed by the `LaborBoard`
 *     grid with `associates` and `processPaths` from the active half.
 *   - The "Manager View" button navigates to
 *     `ROUTES.SHIFT_ROTATION_ASSIGNMENTS` via `useNavigationControl`.
 */
const AssociateViewPage: React.FC = () => {
  const { selectedHalf } = useAppSelector(selectStationAssignment);
  const historicalPlanData = useAppSelector(selectHistoricalPlanData);

  const { navigate } = useNavigationControl();

  // Guard: redirect if no historical plan data is available (direct URL access).
  if (!historicalPlanData) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  const handleManagerViewClick = () => {
    navigate(-1);
  };

  const half = selectedHalf || DEFAULT_SHIFT_HALF_OPTIONS[0].value;
  const activeHalf = historicalPlanData[half as keyof Pick<GetStationAssignmentsResponse, 'firstHalfStationAssignment' | 'secondHalfStationAssignment'>];

  return (
    <Box
      className="associate-view-page"
      spacingInset="400"
      data-testid="associate-view-page"
    >
      <Box
        className="associate-view-page__header"
        data-testid="associate-view-header"
      >
        <Box className="associate-view-page__title">
          <Heading level={1} type="h300">
            Labor board
          </Heading>
        </Box>
        <Box className="associate-view-page__actions">
          <Button
            type="secondary"
            onClick={handleManagerViewClick}
            data-testid="associate-view-manager-button"
          >
            Manager View
          </Button>
        </Box>
      </Box>

      {activeHalf && (
        <LaborBoard
          associates={activeHalf.associates}
          processPaths={activeHalf.processPaths}
        />
      )}
    </Box>
  );
};

export default AssociateViewPage;

import './index.scss';